// ============================================================
// components/Navbar.jsx — Role-Based Navigation + Notifications
// ============================================================

import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications, useBusinessSettings } from "../hooks/useFirestore";

export default function Navbar({ currentView, setCurrentView }) {
  const { user, isAdmin, isAuthenticated } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { settings } = useBusinessSettings();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const navigate = (view) => {
    setCurrentView(view);
    setMobileMenu(false);
  };

  const handleNotifClick = async (notif) => {
    if (!notif.isRead) await markAsRead(notif.id);
    // Navigate based on notification type
    if (notif.bookingId) {
      navigate(isAdmin ? "admin-bookings" : "my-bookings");
    }
    setShowNotifs(false);
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  const customerItems = [
    { id: "home", label: "Home" },
    { id: "menu", label: "Menu" },
    { id: "book", label: "Book" },
    { id: "loyalty", label: "Rewards" },
    { id: "about", label: "About" },
  ];

  const adminItems = [
    { id: "admin-dashboard", label: "Dashboard" },
    { id: "admin-products", label: "Products" },
    { id: "admin-bookings", label: "Bookings" },
    { id: "admin-customers", label: "Customers" },
    { id: "admin-loyalty", label: "Loyalty" },
    { id: "admin-reviews", label: "Reviews" },
    { id: "admin-campaigns", label: "Campaigns" },
    { id: "admin-financials", label: "Financials" },
    { id: "admin-settings", label: "Settings" },
  ];

  const navItems = isAdmin && currentView.startsWith("admin-") ? adminItems : customerItems;

  return (
    <>
      {settings?.announcementBanner && !bannerDismissed && (
        <div style={{
          background: "var(--chai)", color: "white", textAlign: "center",
          padding: "0.5rem 2rem", fontSize: "0.88rem", fontWeight: 500,
          position: "relative", zIndex: 201,
        }}>
          {settings.announcementBanner}
          <button onClick={() => setBannerDismissed(true)} style={{
            position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", color: "white", cursor: "pointer",
            fontSize: "1.1rem", fontWeight: 700, opacity: 0.7,
          }}>✕</button>
        </div>
      )}
    <nav className="top-nav">
      <div className="nav-inner">
        <div className="logo" onClick={() => navigate(isAdmin ? "admin-dashboard" : "home")} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
            <img src="/logo.jpg" alt="Chaitimecrunch" style={{ width: "108%", height: "108%", objectFit: "cover", marginLeft: "-4%", marginTop: "-4%" }} />
          </div>
          Chaitime<span>crunch</span>
        </div>

        <div className={`nav-links ${mobileMenu ? "open" : ""}`}>
          {navItems.map((item) => (
            <button key={item.id}
              className={`nav-link ${currentView === item.id ? "active" : ""}`}
              onClick={() => navigate(item.id)}>
              {item.label}
            </button>
          ))}

          {isAuthenticated && !currentView.startsWith("admin-") && (
            <>
              <button className={`nav-link ${currentView === "my-bookings" ? "active" : ""}`}
                onClick={() => navigate("my-bookings")}>
                My Orders
              </button>
              <button className={`nav-link ${currentView === "profile" ? "active" : ""}`}
                onClick={() => navigate("profile")}>
                Profile
              </button>
            </>
          )}

          {!isAuthenticated && (
            <button className={`nav-link ${currentView === "auth" ? "active" : ""}`}
              onClick={() => navigate("auth")}>
              Sign In
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {/* Notification Bell */}
          {isAuthenticated && (
            <div ref={notifRef} style={{ position: "relative" }}>
              <button onClick={() => setShowNotifs(!showNotifs)} style={{
                background: "none", border: "none", cursor: "pointer", position: "relative",
                padding: "0.4rem", borderRadius: 8, color: "var(--espresso)",
                display: "flex", alignItems: "center",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: 0, right: 0,
                    background: "var(--berry)", color: "white", fontSize: "0.65rem",
                    fontWeight: 700, minWidth: 18, height: 18, borderRadius: 9,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid var(--cream)",
                  }}>
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {showNotifs && (
                <div style={{
                  position: "fixed", top: 60, right: 8, left: 8,
                  maxWidth: 360, marginLeft: "auto",
                  maxHeight: "70vh", overflowY: "auto",
                  background: "var(--card-bg, #fff)", borderRadius: 12,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.15)", border: "1px solid rgba(196,136,58,0.12)",
                  zIndex: 300,
                }}>
                  {/* Header */}
                  <div style={{
                    padding: "0.75rem 1rem", borderBottom: "1px solid rgba(0,0,0,0.06)",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: "1rem" }}>
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <button onClick={async () => { await markAllAsRead(); }} style={{
                        background: "none", border: "none", color: "var(--chai)",
                        fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
                      }}>
                        Mark all read
                      </button>
                    )}
                  </div>

                  {/* Notification List */}
                  {notifications.length === 0 ? (
                    <div style={{ padding: "2rem 1rem", textAlign: "center", color: "var(--cocoa)", fontSize: "0.88rem" }}>
                      No notifications yet
                    </div>
                  ) : (
                    notifications.slice(0, 15).map((n) => (
                      <div key={n.id} onClick={() => handleNotifClick(n)} style={{
                        padding: "0.75rem 1rem",
                        borderBottom: "1px solid rgba(0,0,0,0.04)",
                        cursor: "pointer",
                        background: n.isRead ? "transparent" : "rgba(196,136,58,0.04)",
                        transition: "background 0.15s",
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(196,136,58,0.08)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = n.isRead ? "transparent" : "rgba(196,136,58,0.04)"}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: "0.85rem", fontWeight: n.isRead ? 400 : 700,
                              color: "var(--espresso)", marginBottom: 2,
                            }}>
                              {n.title}
                            </div>
                            <div style={{
                              fontSize: "0.8rem", color: "var(--cocoa)",
                              lineHeight: 1.4, display: "-webkit-box",
                              WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}>
                              {n.body}
                            </div>
                            {n.imageURL && (
                              <img src={n.imageURL} alt="" style={{
                                width: "100%", maxHeight: 120, objectFit: "cover",
                                borderRadius: 6, marginTop: 6,
                              }} />
                            )}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                            <span style={{ fontSize: "0.7rem", color: "var(--cocoa)", whiteSpace: "nowrap" }}>
                              {formatTime(n.createdAt)}
                            </span>
                            {!n.isRead && (
                              <div style={{
                                width: 8, height: 8, borderRadius: "50%",
                                background: "var(--chai)",
                              }} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {isAdmin && (
            <button className={`admin-toggle ${currentView.startsWith("admin-") ? "on" : "off"}`}
              onClick={() => navigate(currentView.startsWith("admin-") ? "home" : "admin-dashboard")}>
              {currentView.startsWith("admin-") ? "Exit Admin" : "Admin"}
            </button>
          )}
          <button className="mobile-menu-btn" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </nav>
    </>
  );
}